/**
 * The handoff's line-art icon set, ported path-for-path from its
 * `side-nav.jsx` (CAE-1538).
 *
 * Lives in `ui/` rather than beside the rail because both the rail and the
 * logout button draw from it, and `layout/` already depends on `ui/` — putting
 * them the other way round had `ui/admin-logout-button` reaching back into
 * `layout/`, which only resolved by bypassing that folder's barrel.
 *
 * These are line art — `fill: none` with a 1.3–1.6 stroke — not Material
 * glyphs. `@mui/icons-material`'s outlined set draws the same *concepts*
 * (trophy, medal, book) as filled outline shapes in a visibly heavier idiom,
 * so the rail read as Material rather than as the design. The stroke widths
 * below are the handoff's own and vary per icon on purpose; they are not a
 * rounding slip to normalise.
 *
 * They stay MUI `SvgIcon`s so the rail keeps sizing them through `fontSize`
 * off `theme.tokens.navRail.iconSize`, exactly as the Material icons were.
 */
import SvgIcon from '@mui/material/SvgIcon';
import type { SvgIconProps } from '@mui/material/SvgIcon';

/**
 * `MuiSvgIcon-root` sets `fill: currentColor`, which would flood these outline
 * shapes solid — and a `fill` attribute cannot beat that class, so the override
 * has to come through `sx`. Children that do want a fill (the Assets lens)
 * carry their own attribute, which wins over the inherited none.
 */
const LineIcon = ({ sx, ...props }: SvgIconProps) => (
  <SvgIcon
    viewBox="0 0 24 24"
    {...props}
    sx={[{ fill: 'none' }, ...(Array.isArray(sx) ? sx : [sx])]}
  />
);

export const ContestsIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <path
      d="M8 4h8v3a4 4 0 11-8 0V4z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M5 6h3M16 6h3M12 11v3M9 17h6M9 20h6"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </LineIcon>
);

export const PrizeTiersIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <circle cx="12" cy="9" r="5" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M9 13l-2 7 5-3 5 3-2-7"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </LineIcon>
);

/**
 * Sports — a ball on a court line, the rail's entry for the Sports section
 * (CAE-1594). Drawn in the handoff's idiom rather than ported from it: the
 * back office's Sport screens have no designs, and this set grows with the
 * screens that reach for it.
 */
export const SportsIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M12 4c2.4 2.2 3.6 4.9 3.6 8s-1.2 5.8-3.6 8M12 4c-2.4 2.2-3.6 4.9-3.6 8s1.2 5.8 3.6 8M4.4 9.5h15.2M4.4 14.5h15.2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </LineIcon>
);

/**
 * Basketball and Soccer — the Sport glyphs `SportIcon` picks between
 * (S2W-S07). Drawn in the same idiom as `SportsIcon`, for the same reason: no
 * design draws a Sport's icon.
 */
export const BasketballIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M12 4v16M4 12h16M6.3 6.4c1.8 1.5 2.8 3.4 2.8 5.6s-1 4.1-2.8 5.6M17.7 6.4c-1.8 1.5-2.8 3.4-2.8 5.6s1 4.1 2.8 5.6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </LineIcon>
);

export const SoccerIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M12 8.6l3.2 2.3-1.2 3.8h-4l-1.2-3.8L12 8.6zM12 8.6V4.2M15.2 10.9l4.1-1.4M14 14.7l2.5 3.6M10 14.7l-2.5 3.6M8.8 10.9L4.7 9.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </LineIcon>
);

export const CatalogueIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <path
      d="M4 5a2 2 0 012-2h6v18H6a2 2 0 01-2-2V5zM20 5a2 2 0 00-2-2h-6v18h6a2 2 0 002-2V5z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </LineIcon>
);

export const AssetsIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M7 15l3-3 4 4 3-3 4 4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="9" cy="10" r="1.4" fill="currentColor" />
  </LineIcon>
);

export const UsersIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <circle cx="9" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <circle cx="17" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.4" />
    <path
      d="M15 20c0-2.4 1.6-4 4-4"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </LineIcon>
);

export const ConfigurationIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.6 15 1.65 1.65 0 003.09 14H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9 1.65 1.65 0 004.27 7.18l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.6 1.65 1.65 0 0010 3.09V3a2 2 0 014 0v.09A1.65 1.65 0 0015 4.6a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9c.4.93 1.18 1.6 2.1 1.6H22a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1.4z"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
  </LineIcon>
);

export const QaSeedIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <path
      d="M9 3v6l-5 9a3 3 0 002.6 4.5h10.8A3 3 0 0020 18l-5-9V3M8 3h8"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </LineIcon>
);

export const LogoutIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <path
      d="M14 8V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2h7a2 2 0 002-2v-2M9 12h12m-3-3l3 3-3 3"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </LineIcon>
);

/**
 * The one icon with no handoff source: the prototype has no Operations
 * destination at all — the word appears nowhere in the bundle, whose rail runs
 * Contests / Prize tiers / Catalogue / Assets / Users / Configuration / QA
 * seed. Drawn here in the same idiom (Lucide's wrench — the family the
 * handoff's own gear and logout are themselves drawn from) at the set's 1.6
 * stroke, so it does not read as the one Material glyph in a line-art rail.
 *
 * Worth putting to design on CAE-1536 alongside the rail width.
 */
export const OperationsIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <path
      d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94L14.7 6.3z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </LineIcon>
);

/*
 * ── Inline glyphs ──────────────────────────────────────────────────────────
 *
 * The small icons the shared primitives draw with (CAE-1539), ported from the
 * `Icon` / `MIcon` maps in the handoff's `02-contest-overview/app.jsx` and
 * `shared/modals-shared.jsx`.
 *
 * Same idiom and same 24-unit grid as the rail set above, so they size through
 * `fontSize` at whatever the component asks for — the handoff draws most of
 * these at 13×13, a couple at 11–14. Their stroke widths are heavier than the
 * rail's (2–3.2 on the check) because they render smaller; that is the
 * handoff's own weighting, not a slip to normalise.
 */

/** `Icon.clock` — the age chip's leading glyph. */
export const ClockIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
    <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </LineIcon>
);

/** `Icon.check` — a completed checklist step, and the ready banner's tick. */
export const CheckIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <path
      d="M5 12.5l4.5 4.5L19 7.5"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </LineIcon>
);

/** `Icon.warn` — an outstanding checklist step. A bare bang, no triangle. */
export const WarnIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <path d="M12 5v9M12 18v.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
  </LineIcon>
);

/** `MIcon.refresh` — the market badge's queued-to-sync spinner. */
export const RefreshIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <path
      d="M4 12a8 8 0 0114-5.3M20 12a8 8 0 01-14 5.3M20 4v4h-4M4 20v-4h4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </LineIcon>
);

/** `MIcon.close` — the filter chip's ×, and a modal's dismiss. */
export const CloseIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </LineIcon>
);

/** `MIcon.search` — the leading glyph of a search input. */
export const SearchIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8" />
    <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </LineIcon>
);

/** `MIcon.chev` — a select's disclosure arrow. Points down; rotate to flip it. */
export const ChevronDownIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <path
      d="M7 10l5 5 5-5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </LineIcon>
);

/** `MIcon.cal` — the date input's trailing glyph. */
export const CalendarIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <rect x="3.5" y="5" width="17" height="15" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M3.5 10h17M8 3v4M16 3v4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </LineIcon>
);

/**
 * `MIcon.plus` — the leading glyph on every create action. Drawn at 13×13, and
 * the empty states (CAE-1540) lean on it hardest: the design's primary CTA is
 * "+ Create your first contest", "+ Add fixtures", "+ Add reel", "+ Add prize
 * tier".
 */
export const PlusIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </LineIcon>
);

/**
 * `MIcon.chevR` — the trailing chevron on a list row that is itself a link, and
 * the Contests list's next-page button. Drawn at 13×13 and 12×12 respectively.
 */
export const ChevronRightIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <path
      d="M10 7l5 5-5 5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </LineIcon>
);

/**
 * The previous-page button's glyph — `chevR` mirrored, not the prototype's own
 * `M15 18l-6-6 6-6`.
 *
 * The handoff draws its pagination arrows as a matched pair at 12 units tall,
 * and `chevR` at 10. Taking the prototype's left arrow literally while the
 * right one stays `chevR` pairs a 12-unit glyph with a 10-unit one in the same
 * row at the same `fontSize`, so prev renders visibly larger than next. The
 * pairing is the thing the design is expressing, so this mirrors its partner.
 *
 * Its own path rather than a CSS transform: a rotation would fight the
 * `fontSize` sizing every caller uses.
 */
export const ChevronLeftIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <path
      d="M14 7l-5 5 5 5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </LineIcon>
);

/**
 * The pencil on a Contests list row's edit action, from the same screen's JSX.
 * Drawn at 14×14 on a 1.7 stroke — lighter than the chevrons beside it, which
 * is the handoff's own weighting and not a slip to normalise.
 */
export const EditIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <path
      d="M4 20h4l10-10-4-4L4 16v4zM14 6l4 4"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </LineIcon>
);

/** `Icon.ext` — the external-link mark on a fixture row. Drawn at 11×11. */
export const ExternalLinkIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <path d="M9 6h9v9M18 6l-12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </LineIcon>
);

/** `MIcon.info` — the button that opens a market's or an outcome's popover. Drawn at 12×12. */
export const InfoIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path d="M12 11v5M12 8v.1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </LineIcon>
);

/**
 * `.fx-row-remove`'s glyph — detaching a fixture from a contest (CAE-1550).
 *
 * A bin, drawn at 14×14 in the handoff. Destructive but not deleting: the
 * fixture stays in the catalogue and can be attached again, which is why the
 * row's confirm says "remove from contest" rather than anything stronger.
 */
export const TrashIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <path
      d="M5 7h14M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </LineIcon>
);

/**
 * `.cl-pausebtn`'s glyph — holding a contest from the Contests list (CAE-1543).
 *
 * **Solid, not line art.** The handoff draws this one as two filled bars rather
 * than in the stroked idiom the rest of this file uses
 * (`01-contests-list/contests-list-app.jsx`), so it carries its own `fill` to
 * beat `LineIcon`'s `fill: none` — the same escape the Assets lens takes. Two
 * hairline outlines at 14×14 would read as a pause sign drawn badly rather than
 * as a transport control.
 */
export const PauseIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <rect x="7" y="5" width="4" height="14" rx="1" fill="currentColor" />
    <rect x="13" y="5" width="4" height="14" rx="1" fill="currentColor" />
  </LineIcon>
);

/**
 * `.cl-pausebtn.is-paused`'s glyph — letting a held contest run again.
 *
 * Solid for the same reason as `PauseIcon`, and the pair has to match: a filled
 * pause beside a hairline play would read as two unrelated controls in the one
 * place they swap for each other.
 */
export const PlayIcon = (props: SvgIconProps) => (
  <LineIcon {...props}>
    <path d="M8 5v14l11-7-11-7z" fill="currentColor" />
  </LineIcon>
);
